import { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { FormInput } from '../ui/FormInput';
import { isEmailUnique } from '../../utils/validation';
import { db } from '../../lib/firebase';

export function ProfileSettings() {
  const { user, updateProfile, changePassword } = useAuth();
  const [formData, setFormData] = useState({
    username: user?.username || '',
    email: user?.email || '',
    phone: user?.phone || ''
  });
  const [isEditing, setIsEditing] = useState(false);
  const [message, setMessage] = useState('');

  const [passwordData, setPasswordData] = useState({
    newPassword: '',
    confirmPassword: ''
  });
  const [passwordMessage, setPasswordMessage] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setMessage('');
    
    try {
      // Vérifier si l'email a changé et s'il est unique
      if (formData.email !== user?.email) {
        const isUnique = await isEmailUnique(formData.email, db, user?.id);
        if (!isUnique) {
          setMessage('Cet email est déjà utilisé par un autre utilisateur');
          return;
        }
      }
      
      // Exclure le téléphone de la mise à jour (on ne l'envoie pas)
      const { phone, ...dataToUpdate } = formData;
      
      await updateProfile(dataToUpdate);
      setMessage('Profil mis à jour avec succès');
      setIsEditing(false);
    } catch (error) {
      setMessage('Erreur lors de la mise à jour du profil');
    }
  };

  const handlePasswordChange = async (e: React.FormEvent) => {
    e.preventDefault();
    setPasswordMessage('');
    
    // Validation
    if (passwordData.newPassword.length < 6) {
      setPasswordMessage('Le mot de passe doit contenir au moins 6 caractères');
      return;
    }
    
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setPasswordMessage('Les mots de passe ne correspondent pas');
      return;
    }
    
    try {
      await changePassword(passwordData.newPassword);
      setPasswordMessage('Mot de passe changé avec succès');
      setPasswordData({ newPassword: '', confirmPassword: '' });
    } catch (error) {
      setPasswordMessage('Erreur lors du changement de mot de passe');
    }
  };

  return (
    <div className="max-w-2xl mx-auto">
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h2 className="text-xl font-semibold mb-6">Paramètres du profil</h2>

        <form onSubmit={handleSubmit} className="space-y-4">
          <FormInput
            label="Nom d'utilisateur"
            value={formData.username}
            onChange={(e) => setFormData(prev => ({ ...prev, username: e.target.value }))}
            disabled={!isEditing}
            required
          />

          <FormInput
            type="email"
            label="Email"
            value={formData.email}
            onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
            disabled={!isEditing}
            required
          />

          <FormInput
            type="tel"
            label="Numéro de téléphone"
            value={formData.phone}
            onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
            disabled={true}
          />
          <p className="text-xs text-gray-500 -mt-3 mb-4">
            Le numéro de téléphone ne peut pas être modifié car il est utilisé pour la connexion
          </p>

          {message && (
            <div className={`text-sm ${message.includes('Erreur') ? 'text-red-600' : 'text-green-600'}`}>
              {message}
            </div>
          )}

          <div className="flex justify-end space-x-4 pt-4">
            {isEditing ? (
              <>
                <button
                  type="button"
                  onClick={() => setIsEditing(false)}
                  className="px-4 py-2 text-gray-600 hover:text-gray-800"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-[#00665C] text-white rounded-lg hover:bg-[#004d45] focus:outline-none focus:ring-2 focus:ring-[#00665C] focus:ring-offset-2"
                >
                  Enregistrer
                </button>
              </>
            ) : (
              <button
                type="button"
                onClick={() => setIsEditing(true)}
                className="px-4 py-2 bg-[#00665C] text-white rounded-lg hover:bg-[#004d45] focus:outline-none focus:ring-2 focus:ring-[#00665C] focus:ring-offset-2"
              >
                Modifier
              </button>
            )}
          </div>
        </form>
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mt-6">
        <h2 className="text-xl font-semibold mb-6">Changer le mot de passe</h2>
        
        <form onSubmit={handlePasswordChange} className="space-y-4">
          <FormInput
            type="password"
            label="Nouveau mot de passe"
            value={passwordData.newPassword}
            onChange={(e) => setPasswordData(prev => ({ ...prev, newPassword: e.target.value }))}
            placeholder="Minimum 6 caractères"
            required
          />
          
          <FormInput
            type="password"
            label="Confirmer le mot de passe"
            value={passwordData.confirmPassword}
            onChange={(e) => setPasswordData(prev => ({ ...prev, confirmPassword: e.target.value }))}
            placeholder="Retaper le mot de passe"
            required
          />
          
          {passwordMessage && (
            <div className={`text-sm ${passwordMessage.includes('Erreur') ? 'text-red-600' : 'text-green-600'}`}>
              {passwordMessage}
            </div>
          )}
          
          <div className="flex justify-end pt-4">
            <button
              type="submit"
              className="px-4 py-2 bg-[#00665C] text-white rounded-lg hover:bg-[#004d45] focus:outline-none focus:ring-2 focus:ring-[#00665C] focus:ring-offset-2"
            >
              Changer le mot de passe
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}